// One settings group, drawn by the generic renderer (spec/13 §13; #454; regrouped by job 2026-10-07).
//
// For every entry: its value in a control (the layout's purpose-made one, else one built from its schema), a source
// line in plain words — "Using your organisation's setting — 5 minutes", "Changed for this venue · Reset to your
// organisation's 5 minutes", "Set by head office" with why — its help, and, where they apply, the station classes or
// venues that keep a value of their own. The form has one Save, however many resources the group is drawn from, and
// each save sends the ETag its resource was read at (spec/08 §2.5).
//
// **The layout** (`layout.ts`): rows that carry a section are drawn in the layout's sections, in its order, with each
// section's panel (`slots.before`) and closing note (`slots.after`) where the layout puts them; the rarely changed
// ones go in one collapsed "Advanced" card at the end; a dependant nests under its master and collapses with "Turn on
// … to use these" while the master says it does nothing — a master on another screen (wallets, on a venue's) is read
// from there (`externalValueOf`) and shown read-only. Rows without a section (the venue record) keep their own
// sub-headings, consecutive rows sharing one.
//
// **A 412 never loses anybody's work.** When someone saved after this editor read, the form reads the resource again,
// shows the fresh values, and lists the editor's unsaved changes beside them; the editor re-applies them on top of the
// fresh values, or keeps the fresh ones (#403 rule 2).
//
// The draft is plain state rather than react-hook-form's: its fields are the registry's, not the screen's, and a
// field's state here — which resource it belongs to, the version it was read at, a pending reset — is not something
// RHF's dirty tracking can express.
//
// **HQ's locks** (spec/03 §9, spec/14 §14). On HQ's screens an entry may be `lockable` at the screen's own scope; it
// then has a lock box beside its value, and a lock is saved with the value it locks (`locked` on the change). A lock at
// the screen's own scope is HQ's, so it does not make the entry read-only there — it binds the scopes below. A lock
// above the screen still does.

import { useTranslation } from '@clavian/i18n/react';
import { Badge, Button, Checkbox, useToast } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useCallback, useMemo, useRef, useState, type ComponentType, type ReactNode } from 'react';

import { fieldErrors, isStaleWrite, needsSecondFactor, problemCode } from '../api/problems';
import { useProblem } from '../api/use-problem';
import { useSecondFactorGate } from '../auth/second-factor-gate';
import type { Versioned } from '../api/versioned';
import { jsonEqual } from '../lib/json';
import { Notice, Section } from '../ui';
import { formatValue } from './format';
import { copyId } from './labels';
import { holds, type Dependency } from './layout';
import { resolveSchema } from './schema';
import { SettingControl } from './special-controls';
import {
  errorKey,
  type ControlProps,
  type RenderableSetting,
  type SettingChange,
  type SettingLabels,
  type SettingsPart,
  type SourceWords,
  type ValueSource,
} from './types';
import { formatWithUnit } from './units';

/** Where the organisation's settings are, for a row set there (a venue screen's "Change it in organisation settings"). */
export interface OrganizationLink {
  readonly to: string;
  readonly params: Readonly<Record<string, string>>;
  /** The job group a key sits in, for the link's group. */
  readonly groupOf: (key: string) => string;
}

export interface SettingsFormProps {
  /** The resources the group is drawn from, in the order their entries are shown. */
  readonly parts: readonly SettingsPart[];
  readonly labels: SettingLabels;
  /** Controls a schema asks for by name (`x-clavian-control`), e.g. the venue's photo uploads. */
  readonly custom?: Readonly<Record<string, ComponentType<ControlProps>>>;
  /** Said above the form when nothing in it can be changed, and why. */
  readonly notice?: ReactNode;
  /** Where a value comes from, in this screen's words; the venue console's by default. */
  readonly sources?: SourceWords;
  /**
   * Drawn under the entries: a preview built from the values on the screen — the editor's unsaved ones included —
   * by key. `valueOf` is `undefined` for a key the screen does not show.
   */
  readonly aside?: (valueOf: (key: string) => unknown) => ReactNode;
  /** The layout's sections for this group, in order: titled, each with an optional panel. */
  readonly sections?: readonly { readonly id: string; readonly title: string }[];
  /** A section's panel, drawn before its card; and a note at the end of its card, from the values on the screen. */
  readonly slots?: {
    readonly before?: (sectionId: string) => ReactNode;
    /** Words at the head of the section's card: how its settings work together. */
    readonly note?: (sectionId: string) => ReactNode;
    readonly after?: (sectionId: string, valueOf: (key: string) => unknown) => ReactNode;
    /** A panel drawn after the section's card: the tax categories, under the tax settings. */
    readonly end?: (sectionId: string) => ReactNode;
  };
  /** The value of a setting this form does not show — a master on another screen — or undefined when unknown. */
  readonly externalValueOf?: (key: string) => unknown;
  /** The label of a setting this form does not show. */
  readonly externalLabel?: (key: string) => string;
  /** Where the organisation's own settings are, when this member may open them. */
  readonly organizationLink?: OrganizationLink;
  /** A key to bring into view: the disclosure holding it opens. */
  readonly reveal?: string;
  /** Stations → Classes, where a class's own values are changed (spec/13 §12). */
  readonly classesLink?: { readonly to: string; readonly params: Readonly<Record<string, string>> };
}

/** Is this entry locked at the screen's own scope — HQ's lock, here? */
const lockedHere = (entry: RenderableSetting): boolean => entry.lockable !== undefined && entry.lockedAt === entry.lockable;

type Bases = Readonly<Record<string, Versioned<readonly RenderableSetting[]>>>;

interface Row {
  readonly id: string;
  readonly part: string;
  readonly entry: RenderableSetting;
}

interface ConflictRow {
  readonly id: string;
  readonly label: string;
  readonly theirs: string;
  readonly yours: string;
  /** The fresh entry can no longer be changed here — locked, or the role changed. */
  readonly nowReadOnly: boolean;
}

interface Conflict {
  readonly rows: readonly ConflictRow[];
  readonly pending: Readonly<Record<string, SettingChange>>;
}

const rowId = (part: string, key: string) => `${part}:${key}`;

function rowsOf(parts: readonly SettingsPart[], bases: Bases): Row[] {
  return parts.flatMap((part) =>
    (bases[part.id]?.data ?? []).map((entry) => ({ id: rowId(part.id, entry.key), part: part.id, entry })),
  );
}

const partOfRow = (id: string) => id.slice(0, id.indexOf(':'));

export function SettingsForm({
  parts,
  labels,
  custom = {},
  notice,
  sources: givenSources,
  aside,
  sections: layoutSections,
  slots,
  externalValueOf,
  externalLabel,
  organizationLink,
  reveal,
  classesLink,
}: SettingsFormProps): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const toast = useToast();
  const secondFactor = useSecondFactorGate();
  const sources = useMemo<Required<SourceWords>>(
    () => ({
      badge: givenSources?.badge ?? ((source) => t(`settings.source.${source}`)),
      name: givenSources?.name ?? ((source) => t(`settings.sourceName.${source}`)),
      using: givenSources?.using ?? ((source, value) => t(`settings.using.${source}`, { value })),
      changed: givenSources?.changed ?? '',
      resetTo: givenSources?.resetTo ?? ((source, value) => t(`settings.resetTo.${source}`, { value })),
    }),
    [givenSources, t],
  );

  // The version each resource's unsaved changes were made against. A resource with none follows
  // the newest version it is given — another tab's save, a refetch — as it comes; one with unsaved
  // changes keeps the version they were made against, so saving them is a 412 and the conflict is
  // shown, never a silent overwrite of someone else's save.
  const [pinned, setPinned] = useState<Bases>({});
  const [draft, setDraft] = useState<Readonly<Record<string, unknown>>>({});
  const [resets, setResets] = useState<ReadonlySet<string>>(new Set());
  // HQ's lock boxes, by row: what the editor has ticked, where it differs from what is stored.
  const [locks, setLocks] = useState<Readonly<Record<string, boolean>>>({});
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<{ message: string; requestId?: string | undefined } | null>(null);
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  // The disclosures: the group's Advanced card, and dependants shown although their master says they do nothing.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  // Entries whose control holds something it cannot turn into a value yet (the JSON editor).
  const [unparsed, setUnparsed] = useState<ReadonlySet<string>>(new Set());
  // Stable, so a control's effect that reports through it runs when its state changes, not on
  // every render of the form.
  const markUnparsed = useCallback((key: string, bad: boolean) => {
    setUnparsed((current) => {
      if (current.has(key) === bad) return current;
      const next = new Set(current);
      if (bad) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  const statusRef = useRef<HTMLParagraphElement>(null);
  const saveRef = useRef<HTMLButtonElement>(null);
  /** Focus the save bar's status once the next render is on screen — where a keyboard user continues. */
  const focusStatus = () => requestAnimationFrame(() => statusRef.current?.focus());

  const touched = useMemo(
    () => new Set([...Object.keys(draft), ...resets, ...Object.keys(locks)].map(partOfRow)),
    [draft, resets, locks],
  );
  const bases = useMemo<Bases>(
    () =>
      Object.fromEntries(
        parts.map((part) => [part.id, (touched.has(part.id) ? pinned[part.id] : undefined) ?? { data: part.entries, etag: part.etag }]),
      ),
    [parts, pinned, touched],
  );
  const rows = useMemo(() => rowsOf(parts, bases), [parts, bases]);

  /** The first change to a resource pins the version it is made against. */
  const pin = (partId: string) => {
    if (touched.has(partId)) return;
    const base = bases[partId];
    if (base !== undefined) setPinned((current) => ({ ...current, [partId]: base }));
  };

  const valueOf = (row: Row): unknown => (resets.has(row.id) ? row.entry.inherited?.value : row.id in draft ? draft[row.id] : row.entry.value);
  /** The value on the screen for a key — this form's, the editor's unsaved one included — else the master's elsewhere. */
  const valueOfKey = (key: string): unknown => {
    const row = rows.find((candidate) => candidate.entry.key === key);
    return row === undefined ? externalValueOf?.(key) : valueOf(row);
  };
  /** Whether the row's lock box is ticked: the editor's choice, else what is stored. */
  const lockOf = (row: Row): boolean => locks[row.id] ?? lockedHere(row.entry);
  const changesByPart = (): Map<string, SettingChange[]> => {
    const byPart = new Map<string, SettingChange[]>();
    for (const row of rows) {
      let change: SettingChange | undefined;
      if (resets.has(row.id)) change = { key: row.entry.key, reset: true };
      else {
        const valueChanged = row.id in draft && !jsonEqual(draft[row.id], row.entry.value);
        // A lock is set on the value it locks, so a lock alone sends the value as it stands.
        const lockChanged = row.entry.lockable !== undefined && row.id in locks && locks[row.id] !== lockedHere(row.entry);
        if (valueChanged || lockChanged) {
          change = { key: row.entry.key, value: row.id in draft ? draft[row.id] : row.entry.value, ...(lockChanged ? { locked: lockOf(row) } : {}) };
        }
      }
      if (change !== undefined) byPart.set(row.part, [...(byPart.get(row.part) ?? []), change]);
    }
    return byPart;
  };
  const pendingCount = [...changesByPart().values()].reduce((sum, changes) => sum + changes.length, 0);

  const editable = rows.some((row) => row.entry.editable);

  // A key named by the URL or the search opens the disclosure that holds it, so its row is drawn to be revealed. Done
  // while rendering, once per key (React's "adjust state when a prop changes"), so the row is there for the reveal.
  const [revealSeen, setRevealSeen] = useState<string | undefined>(undefined);
  if (reveal !== revealSeen) {
    setRevealSeen(reveal);
    const row = reveal === undefined ? undefined : rows.find((candidate) => candidate.entry.key === reveal);
    if (row?.entry.advanced === true) setAdvancedOpen(true);
    const masters = (row?.entry.dependsOn ?? []).map((dependency) => dependency.key);
    if (masters.length > 0) setExpanded((current) => new Set([...current, ...masters]));
  }

  const set = (row: Row, value: unknown) => {
    pin(row.part);
    setDraft((current) => ({ ...current, [row.id]: value }));
    setStatus(null);
  };
  const toggleReset = (row: Row, on: boolean) => {
    pin(row.part);
    setResets((current) => {
      const next = new Set(current);
      if (on) next.add(row.id);
      else next.delete(row.id);
      return next;
    });
    setDraft((current) => {
      const { [row.id]: _dropped, ...rest } = current;
      return rest;
    });
    // A reset removes the value and its lock together.
    setLocks((current) => {
      const { [row.id]: _dropped, ...rest } = current;
      return rest;
    });
  };
  const toggleLock = (row: Row, on: boolean) => {
    pin(row.part);
    setLocks((current) => {
      if (on === lockedHere(row.entry)) {
        const { [row.id]: _dropped, ...rest } = current;
        return rest;
      }
      return { ...current, [row.id]: on };
    });
    setStatus(null);
  };
  const discard = () => {
    setDraft({});
    setResets(new Set());
    setLocks({});
    setErrors({});
    setFailure(null);
    setStatus(null);
  };

  // The shared copy for the server's code (locked by HQ, plan does not allow it, not permitted, a name already taken),
  // and the generic line only for a code with no copy; the reference goes with the generic one alone. A validation
  // answer keeps this screen's own line, which points at the fields marked below.
  const failureView = (error: unknown): { message: string; reference: string | undefined } => {
    const view = problem.view(error, { fallback: t('errors.generic') });
    return { message: view.isValidation ? t('settings.errors.validation') : view.message, reference: view.reference };
  };
  const failureMessage = (error: unknown): string => failureView(error).message;
  const failureOf = (error: unknown): { message: string; requestId?: string } => {
    const { message, reference } = failureView(error);
    return reference === undefined ? { message } : { message, requestId: reference };
  };

  const save = async () => {
    const byPart = changesByPart();
    if (byPart.size === 0 || saving) return;
    if (unparsed.size > 0) {
      setFailure({ message: t('settings.errors.unparsed') });
      return;
    }
    const fromSaveButton = document.activeElement === saveRef.current;
    setSaving(true);
    setFailure(null);
    setErrors({});
    setStatus(null);

    // What each saved row was saved as: an edit made to the row while the save was in flight is not it.
    const saved = new Map<string, SettingChange>();
    // The versions this save learnt. Merged into the pins, never replacing them: a pin taken for
    // another resource while the save was in flight stays.
    const learnt: Record<string, Versioned<readonly RenderableSetting[]>> = {};
    try {
      for (const part of parts) {
        const changes = byPart.get(part.id);
        const base = bases[part.id];
        if (changes === undefined || base === undefined) continue;
        try {
          // The part tells the query cache too (`onVersion`), so the next render's props agree.
          learnt[part.id] = await part.save(changes, base.etag);
          for (const change of changes) saved.set(rowId(part.id, change.key), change);
        } catch (error) {
          if (isStaleWrite(error)) {
            const fresh = await part.reload();
            learnt[part.id] = fresh;
            showConflict(part, changes, fresh);
            continue;
          }
          if (needsSecondFactor(error)) {
            secondFactor.require();
            break;
          }
          const filed: Record<string, string> = {};
          for (const entry of fieldErrors(error)) {
            const key = part.fieldKey?.(entry.field, changes) ?? entry.field;
            filed[key] = entry.message;
          }
          const code = problemCode(error);
          const concerned = code === undefined ? undefined : part.problemField?.(code);
          if (concerned !== undefined) filed[concerned] = failureMessage(error);
          if (code === 'setting_locked') {
            // HQ locked a setting while it was being edited: read the screen again so the lock
            // shows, and let go of the changes it now refuses. The rest stay, on the fresh version.
            const fresh = await part.reload();
            learnt[part.id] = fresh;
            const refused = new Set(fresh.data.filter((entry) => !entry.editable).map((entry) => rowId(part.id, entry.key)));
            setDraft((current) => Object.fromEntries(Object.entries(current).filter(([id]) => !refused.has(id))));
            setResets((current) => new Set([...current].filter((id) => !refused.has(id))));
            setLocks((current) => Object.fromEntries(Object.entries(current).filter(([id]) => !refused.has(id))));
          }
          setErrors(filed);
          setFailure(failureOf(error));
          break;
        }
      }
    } catch (error) {
      setFailure(failureOf(error));
    } finally {
      setPinned((current) => ({ ...current, ...learnt }));
      // What was saved is now the base; what was not stays in the draft.
      setDraft((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([id, value]) => {
            const change = saved.get(id);
            return change === undefined || change.reset === true || !jsonEqual(value, change.value);
          }),
        ),
      );
      setResets((current) => new Set([...current].filter((id) => saved.get(id)?.reset !== true)));
      // A lock saved as ticked is the stored lock now; a reset took the lock with it.
      setLocks((current) =>
        Object.fromEntries(
          Object.entries(current).filter(([id, locked]) => {
            const change = saved.get(id);
            return change === undefined || (change.reset !== true && change.locked !== locked);
          }),
        ),
      );
      setSaving(false);
      if (saved.size > 0) toast.show({ title: t('settings.saved', { count: saved.size }), tone: 'ok' });
      // Save is disabled once there is nothing left to save; focus must not be left on it.
      if (fromSaveButton) focusStatus();
    }
  };

  /** The fresh values are the base now; the editor's changes wait in the panel, not in the fields. */
  function showConflict(part: SettingsPart, changes: readonly SettingChange[], fresh: Versioned<readonly RenderableSetting[]>) {
    const words = formatWords(t);
    const rows: ConflictRow[] = [];
    const pending: Record<string, SettingChange> = {};
    for (const change of changes) {
      const entry = fresh.data.find((candidate) => candidate.key === change.key);
      if (entry === undefined) continue;
      const id = rowId(part.id, change.key);
      const format = (value: unknown) => formatEntry(value, entry, labels, words);
      pending[id] = change;
      const theirs = format(entry.value);
      let yours = change.reset ? t('settings.conflict.yourReset', { value: format(entry.inherited?.value) }) : format(change.value);
      if (change.locked !== undefined) yours = t(change.locked ? 'settings.conflict.andLocked' : 'settings.conflict.andUnlocked', { value: yours });
      rows.push({
        id,
        label: entry.label,
        theirs: entry.lockable !== undefined && lockedHere(entry) ? t('settings.conflict.andLocked', { value: theirs }) : theirs,
        yours,
        nowReadOnly: !entry.editable,
      });
    }
    // A field changed again while the save was in flight keeps its newer value.
    setDraft((current) =>
      Object.fromEntries(
        Object.entries(current).filter(([id, value]) => {
          const change = pending[id];
          return change === undefined || (change.reset !== true && !jsonEqual(value, change.value));
        }),
      ),
    );
    setResets((current) => new Set([...current].filter((id) => pending[id]?.reset !== true)));
    // A lock box waits in the panel with its value, unless it was ticked again since.
    setLocks((current) =>
      Object.fromEntries(Object.entries(current).filter(([id, locked]) => pending[id] === undefined || pending[id]?.locked !== locked)),
    );
    setConflict((current) => ({
      rows: [...(current?.rows ?? []).filter((row) => !(row.id in pending)), ...rows],
      pending: { ...(current?.pending ?? {}), ...pending },
    }));
  }

  const reapply = () => {
    if (conflict === null) return;
    const nextDraft: Record<string, unknown> = { ...draft };
    const nextResets = new Set(resets);
    const nextLocks: Record<string, boolean> = { ...locks };
    for (const [id, change] of Object.entries(conflict.pending)) {
      const row = rows.find((candidate) => candidate.id === id);
      // Not what the lock refuses, and not over a newer edit made while the save was in flight.
      if (row === undefined || !row.entry.editable || id in draft) continue;
      if (change.reset) nextResets.add(id);
      else nextDraft[id] = change.value;
      if (change.locked !== undefined && row.entry.lockable !== undefined && !(id in locks) && change.locked !== lockedHere(row.entry)) {
        nextLocks[id] = change.locked;
      }
    }
    // Re-applied changes are made against the fresh version, which is the base now.
    setPinned((current) => ({ ...current, ...bases }));
    setDraft(nextDraft);
    setResets(nextResets);
    setLocks(nextLocks);
    setConflict(null);
    setStatus(t('settings.conflict.reapplied'));
    focusStatus();
  };

  const keepTheirs = () => {
    setConflict(null);
    focusStatus();
  };

  const errorFor = (entryKey: string) => (path: readonly (string | number)[]) => errors[errorKey(entryKey, path)];
  const errorList = Object.entries(errors);

  // -------------------------------------------------------------------------------------------
  // Drawing the rows
  // -------------------------------------------------------------------------------------------

  const keysOnForm = new Set(rows.map((row) => row.entry.key));

  const renderRow = (row: Row, nested = false, titled = false): ReactNode => (
    <SettingRow
      key={row.id}
      row={row}
      nested={nested}
      titled={titled}
      value={valueOf(row)}
      resetPending={resets.has(row.id)}
      labels={labels}
      custom={custom}
      errorAt={errorFor(row.entry.key)}
      onChange={(value) => set(row, value)}
      onReset={(on) => toggleReset(row, on)}
      onUnparsed={markUnparsed}
      locked={lockOf(row)}
      onLock={(on) => toggleLock(row, on)}
      sources={sources}
      valueOf={valueOfKey}
      overridden={row.entry.overriddenBy !== undefined && holds(row.entry.overriddenBy.when, valueOfKey(row.entry.overriddenBy.key))}
      organizationLink={organizationLink}
      classesLink={classesLink}
    />
  );

  /** A master's label: its row's, or the copy for a setting on another screen. */
  const labelOf = (key: string): string => rows.find((row) => row.entry.key === key)?.entry.label ?? externalLabel?.(key) ?? key;

  /**
   * The rows of one card as a tree: each master followed by its sub-options (`under`) and its dependants, which
   * collapse while the master says they do nothing. A dependant whose master is not on this form at all nests under
   * a read-only line for that master (wallets, on a venue's screen).
   */
  const renderTree = (cardRows: readonly Row[]): ReactNode[] => {
    const inCard = new Set(cardRows.map((row) => row.entry.key));
    const parentOf = (row: Row): string | undefined => {
      if (row.entry.under !== undefined && inCard.has(row.entry.under)) return row.entry.under;
      const first = row.entry.dependsOn?.find((dependency) => inCard.has(dependency.key) || !keysOnForm.has(dependency.key));
      return first?.key;
    };
    const children = new Map<string, Row[]>();
    const top: (Row | { readonly virtual: string })[] = [];
    for (const row of cardRows) {
      const parent = parentOf(row);
      if (parent === undefined) {
        top.push(row);
        continue;
      }
      if (!inCard.has(parent) && !children.has(parent)) top.push({ virtual: parent });
      children.set(parent, [...(children.get(parent) ?? []), row]);
    }
    const drawChildren = (master: string): ReactNode => {
      const kids = children.get(master) ?? [];
      if (kids.length === 0) return null;
      const subOptions = kids.filter((kid) => kid.entry.under === master);
      const dependants = kids.filter((kid) => kid.entry.under !== master);
      const condition = dependants.flatMap((kid) => kid.entry.dependsOn ?? []).find((dependency) => dependency.key === master);
      const value = valueOfKey(master);
      const unmet = condition !== undefined && value !== undefined && !holds(condition.when, value);
      const open = !unmet || expanded.has(master);
      return (
        <div className="console-setting-children">
          {subOptions.length > 0 ? <SubOptionsNote master={master} /> : null}
          {subOptions.map((kid) => renderRow(kid, true))}
          {dependants.length === 0 ? null : unmet ? (
            <DependantsCollapsed
              master={master}
              count={dependants.length}
              open={open}
              onToggle={() =>
                setExpanded((current) => {
                  const next = new Set(current);
                  if (next.has(master)) next.delete(master);
                  else next.add(master);
                  return next;
                })
              }
            >
              {dependants.map((kid) => renderRow(kid, true))}
            </DependantsCollapsed>
          ) : (
            dependants.map((kid) => renderRow(kid, true))
          )}
        </div>
      );
    };
    return top.map((node) =>
      'virtual' in node ? (
        <div key={`virtual:${node.virtual}`} className="console-setting-group">
          <ElsewhereMaster
            settingKey={node.virtual}
            label={labelOf(node.virtual)}
            value={externalValueOf?.(node.virtual)}
            dependency={(children.get(node.virtual) ?? [])[0]?.entry.dependsOn?.find((dependency) => dependency.key === node.virtual)}
            organizationLink={organizationLink}
          />
          {drawChildren(node.virtual)}
        </div>
      ) : children.has(node.entry.key) ? (
        <div key={node.id} className="console-setting-group">
          {renderRow(node)}
          {drawChildren(node.entry.key)}
        </div>
      ) : (
        renderRow(node)
      ),
    );
  };

  const laidOut = rows.some((row) => row.entry.sectionId !== undefined);
  let body: ReactNode;
  if (!laidOut) {
    // The venue record, HQ's brand: their own sub-headings, consecutive rows sharing a card.
    body = sectionsOf(rows).map((section) => (
      <Section key={section.key} title={section.title} titleAs="h3" body="rows">
        {section.rows.map((row) => renderRow(row, false, section.rows.length === 1 && row.entry.label === section.title))}
      </Section>
    ));
  } else {
    const valueOfAny = (key: string) => valueOfKey(key);
    const order = layoutSections ?? [...new Map(rows.map((row) => [row.entry.sectionId ?? '', { id: row.entry.sectionId ?? '', title: row.entry.section ?? '' }])).values()];
    const primary = rows.filter((row) => row.entry.advanced !== true);
    const advanced = rows.filter((row) => row.entry.advanced === true);
    body = (
      <>
        {order.map((section) => {
          const cardRows = primary.filter((row) => row.entry.sectionId === section.id);
          const before = slots?.before?.(section.id);
          const after = slots?.after?.(section.id, valueOfAny);
          const end = slots?.end?.(section.id);
          const note = slots?.note?.(section.id);
          const nothing = (node: ReactNode) => node === undefined || node === null;
          if (cardRows.length === 0 && nothing(before) && nothing(after) && nothing(end)) return null;
          return (
            <div key={section.id} className="console-settings-section" data-section={section.id}>
              {before}
              {cardRows.length > 0 || (after !== undefined && after !== null) ? (
                <Section title={section.title} titleAs="h3" body="rows">
                  {note === undefined || note === null ? null : <div className="console-settings-section__note">{note}</div>}
                  {renderTree(cardRows)}
                  {after === undefined || after === null ? null : <div className="console-settings-section__after">{after}</div>}
                </Section>
              ) : null}
              {end}
            </div>
          );
        })}
        {/* Sections the layout did not name (a setting no layout knows) still get a card. */}
        {[...new Set(primary.map((row) => row.entry.sectionId ?? ''))]
          .filter((id) => !order.some((section) => section.id === id))
          .map((id) => (
            <Section key={id} title={primary.find((row) => row.entry.sectionId === id)?.entry.section} titleAs="h3" body="rows">
              {renderTree(primary.filter((row) => row.entry.sectionId === id))}
            </Section>
          ))}
        {advanced.length === 0 ? null : (
          <section className="console-section console-advanced" aria-labelledby="console-advanced-title">
            <header className="console-section__head">
              <div className="console-section__heading">
                <h3 id="console-advanced-title" className="console-section__title">
                  {t('settings.advanced.title')}
                </h3>
              </div>
              <Button variant="ghost" size="sm" aria-expanded={advancedOpen} aria-controls="console-advanced-body" onClick={() => setAdvancedOpen((open) => !open)}>
                {advancedOpen ? t('settings.advanced.hide') : t('settings.advanced.show', { count: advanced.length })}
              </Button>
            </header>
            <div id="console-advanced-body" className="console-section__body" hidden={!advancedOpen}>
              {advancedOpen
                ? [...new Map(advanced.map((row) => [row.entry.sectionId ?? '', row.entry.section ?? ''])).entries()].map(([id, title]) => (
                    <div key={id} className="console-advanced__part">
                      <h4 className="console-advanced__title">{title}</h4>
                      {renderTree(advanced.filter((row) => row.entry.sectionId === id))}
                    </div>
                  ))
                : null}
            </div>
          </section>
        )}
      </>
    );
  }

  return (
    <div className="console-settings-form" aria-busy={saving || undefined}>
      {notice ? <Notice role="note">{notice}</Notice> : null}

      {conflict === null ? null : <ConflictPanel rows={conflict.rows} onReapply={reapply} onKeepTheirs={keepTheirs} />}

      {failure === null ? null : (
        <div className="console-alert" role="alert">
          <p>{failure.message}</p>
          {errorList.length > 0 ? (
            <ul>
              {errorList.map(([key, message]) => (
                <li key={key}>
                  {labelForErrorKey(rows, key)}: {message}
                </li>
              ))}
            </ul>
          ) : null}
          {failure.requestId ? <p className="console-muted">{t('errors.reference', { id: failure.requestId })}</p> : null}
        </div>
      )}

      {body}

      {aside === undefined ? null : aside((key) => (rows.some((row) => row.entry.key === key) ? valueOfKey(key) : undefined))}

      {editable ? (
        // Pinned over the form only while there is something to save; otherwise a quiet line at its foot,
        // still the status region that says "Saved." after a save.
        <div className={pendingCount > 0 ? 'console-savebar console-savebar--dirty' : 'console-savebar'}>
          <p className="console-savebar__status" role="status" ref={statusRef} tabIndex={-1}>
            {status ?? (pendingCount > 0 ? t('settings.unsaved', { count: pendingCount }) : t('settings.noChanges'))}
          </p>
          <Button type="button" variant="ghost" disabled={pendingCount === 0 || saving} onClick={discard}>
            {t('settings.discard')}
          </Button>
          <Button ref={saveRef} type="button" variant="primary" busy={saving} disabled={pendingCount === 0} onClick={() => void save()}>
            {t('settings.save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Dependants and masters
// ---------------------------------------------------------------------------------------------

function DependantsCollapsed({
  master,
  count,
  open,
  onToggle,
  children,
}: {
  readonly master: string;
  readonly count: number;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly children: ReactNode;
}) {
  const { t, i18n } = useTranslation('console');
  const why = `settings.depends.${copyId(master)}`;
  const id = `console-dependants-${copyId(master)}`;
  return (
    <div className="console-dependants" data-collapsed={open ? undefined : true}>
      <div className="console-dependants__note">
        <p>{i18n.exists(why, { ns: 'console' }) ? t(why) : t('settings.depends.fallback')}</p>
        <Button variant="ghost" size="sm" aria-expanded={open} aria-controls={id} onClick={onToggle}>
          {open ? t('settings.depends.hide') : t('settings.depends.show', { count })}
        </Button>
      </div>
      <div id={id} hidden={!open}>
        {open ? children : null}
      </div>
    </div>
  );
}

/** What a sub-option is to the setting above it, when that needs saying (a receipt emailed whatever checkout asks). */
function SubOptionsNote({ master }: { readonly master: string }) {
  const { t, i18n } = useTranslation('console');
  const id = `settings.under.${copyId(master)}`;
  return i18n.exists(id, { ns: 'console' }) ? <p className="console-setting-children__note">{t(id)}</p> : null;
}

/** A master on another screen, read-only here: its state, and the way to it for whoever may open it. */
function ElsewhereMaster({
  settingKey,
  label,
  value,
  dependency,
  organizationLink,
}: {
  readonly settingKey: string;
  readonly label: string;
  readonly value: unknown;
  readonly dependency: Dependency | undefined;
  readonly organizationLink: OrganizationLink | undefined;
}) {
  const { t } = useTranslation('console');
  const met = dependency === undefined || value === undefined || holds(dependency.when, value);
  return (
    <div className="console-setting console-setting--elsewhere" data-setting-elsewhere={settingKey}>
      <p className="console-setting__elsewhere">
        {value === undefined ? label : met ? t('settings.depends.elsewhereOn', { setting: label }) : t('settings.depends.elsewhere', { setting: label })}{' '}
        {organizationLink === undefined ? null : (
          <Link to={organizationLink.to} params={{ ...organizationLink.params, group: organizationLink.groupOf(settingKey) }} hash={settingKey}>
            {t('settings.depends.openElsewhere')}
          </Link>
        )}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// One entry
// ---------------------------------------------------------------------------------------------

interface SettingRowProps {
  readonly row: Row;
  /** The panel's heading already names this setting. */
  readonly titled: boolean;
  /** Drawn under its master. */
  readonly nested: boolean;
  readonly value: unknown;
  readonly resetPending: boolean;
  readonly labels: SettingLabels;
  readonly custom: Readonly<Record<string, ComponentType<ControlProps>>>;
  readonly errorAt: (path: readonly (string | number)[]) => string | undefined;
  readonly onChange: (value: unknown) => void;
  readonly onReset: (on: boolean) => void;
  readonly onUnparsed: (key: string, unparsed: boolean) => void;
  /** Whether the lock box is ticked (HQ's screens, `entry.lockable`). */
  readonly locked: boolean;
  readonly onLock: (on: boolean) => void;
  readonly sources: Required<SourceWords>;
  readonly valueOf: (key: string) => unknown;
  /** A master makes this one do nothing just now (strict offline lock). */
  readonly overridden: boolean;
  readonly organizationLink: OrganizationLink | undefined;
  readonly classesLink: SettingsFormProps['classesLink'];
}

function SettingRow({
  row,
  titled,
  nested,
  value,
  resetPending,
  labels,
  custom,
  errorAt,
  onChange,
  onReset,
  onUnparsed,
  locked,
  onLock,
  sources,
  valueOf,
  overridden,
  organizationLink,
  classesLink,
}: SettingRowProps) {
  const { t } = useTranslation('console');
  const { entry } = row;
  const resolved = resolveSchema(entry.schema, entry.context);
  const words = formatWords(t);
  const format = (shown: unknown) => formatEntry(shown, entry, labels, words);
  // Inside a sentence a choice reads without its aside: "Using the default — Light", not "… — Light — recommended".
  const inSentence = (text: string) => text.split(' — ')[0] ?? text;
  const inheritedText = entry.inherited === undefined ? undefined : format(entry.inherited.value);
  const canReset = entry.editable && entry.hasOverride && entry.inherited !== undefined;
  // HQ's own lock, at this screen's scope: it binds the scopes below, not this screen.
  const ownLock = lockedHere(entry);
  const lockedAbove = entry.lockedAt !== undefined && !ownLock;

  // A value head office has locked above this screen is not an input to try: it is read as its value,
  // in words, where the control would be — not a greyed-out field.
  const asValue = (lockedAbove || entry.why === 'organization' || entry.why === 'dated') && !resetPending;

  // The source line: where the value in force comes from, in the intro's words.
  let sourceLine: ReactNode = null;
  if (lockedAbove) {
    sourceLine = (
      <>
        <Badge tone="warn" dot>
          {t('settings.locked.badge')}
        </Badge>
        {entry.lockedAt ? <span className="console-muted">{t(`settings.locked.${entry.lockedAt}`)}</span> : null}
      </>
    );
  } else if (ownLock) {
    sourceLine = (
      <>
        <Badge tone="warn" dot>
          {t('settings.lock.badge')}
        </Badge>
        <span className="console-muted">{t(entry.lockable === 'platform' ? 'settings.lock.bindsPlatform' : 'settings.lock.bindsOrganization')}</span>
      </>
    );
  } else if (resetPending) {
    sourceLine = (
      <span className="console-muted">
        {t('settings.resetPending', { value: inheritedText, source: sources.name(entry.inherited?.source ?? 'default') })}
      </span>
    );
  } else if (entry.hasOverride && entry.source !== undefined) {
    sourceLine = <span className="console-setting__source">{sources.changed !== '' ? sources.changed : t(`settings.changed.${entry.source === 'default' ? 'venue' : entry.source}`)}</span>;
  } else if (entry.source !== undefined) {
    sourceLine = <span className="console-setting__source">{sources.using(entry.source, inSentence(format(entry.value)))}</span>;
  }

  const why =
    entry.why === 'role' ? (
      <span className="console-muted">{t('settings.why.role')}</span>
    ) : entry.why === 'organization' ? (
      <span className="console-muted">
        {t('settings.why.organization')}{' '}
        {organizationLink === undefined ? null : (
          <Link to={organizationLink.to} params={{ ...organizationLink.params, group: organizationLink.groupOf(entry.key) }} hash={entry.key}>
            {t('settings.why.openOrganization')}
          </Link>
        )}
      </span>
    ) : entry.why === 'dated' ? (
      <span className="console-muted">{t('settings.why.dated')}</span>
    ) : null;

  return (
    <div
      className={['console-setting', titled ? 'console-setting--titled' : '', nested ? 'console-setting--nested' : ''].filter(Boolean).join(' ')}
      data-setting={entry.key}
    >
      {asValue ? (
        <div className="clav-field">
          <span className="clav-field__label">{entry.label}</span>
          {entry.description ? <p className="clav-field__description">{entry.description}</p> : null}
          <p className="console-setting__value">{format(value)}</p>
        </div>
      ) : (
        <SettingControl
          entryKey={entry.key}
          schema={resolved.schema}
          context={entry.context}
          nullable={resolved.nullable}
          path={[]}
          label={entry.label}
          description={entry.description}
          value={value}
          onChange={onChange}
          disabled={!entry.editable || resetPending}
          required={!resolved.nullable}
          labels={labels}
          errorAt={errorAt}
          onUnparsed={onUnparsed}
          custom={custom}
          valueOf={valueOf}
          display={entry.display}
          hint={entry.control}
        />
      )}
      {entry.lockable !== undefined && entry.editable ? (
        <Checkbox
          className="console-setting__lock"
          label={t(`settings.lock.${entry.lockable}`)}
          checked={locked && !resetPending}
          disabled={resetPending}
          onCheckedChange={(checked) => onLock(checked === true)}
        />
      ) : null}
      <div className="console-setting__meta">
        {overridden ? (
          <p className="console-setting__overridden" role="note">
            {t(`settings.overridden.${copyId(entry.overriddenBy?.key ?? '')}`)}
          </p>
        ) : null}
        {sourceLine}
        {/* One button in one place, whose words change: pressing it must not drop focus. */}
        {resetPending || canReset ? (
          <Button size="sm" variant="ghost" onClick={() => onReset(!resetPending)}>
            {resetPending ? t('settings.undoReset') : sources.resetTo(entry.inherited?.source ?? 'default', inSentence(inheritedText ?? ''))}
          </Button>
        ) : null}
        {why}
        {!lockedAbove && entry.readOnlyReason ? <span className="console-muted">{entry.readOnlyReason}</span> : null}
        {/* The registry default, when the source line does not already say it is in use. */}
        {entry.unitDefault !== undefined && !lockedAbove && entry.source !== 'default' ? (
          <span className="console-muted">{t('settings.defaultValue', { value: format(entry.unitDefault.value) })}</span>
        ) : null}
        {entry.classValues !== undefined ? (
          <span className="console-muted console-setting__places">
            {t('settings.classValues', {
              count: entry.classValues.length,
              classes: entry.classValues.map((place) => `${place.name} (${format(place.value)})`).join(', '),
            })}{' '}
            {classesLink === undefined ? null : (
              <Link to={classesLink.to} params={classesLink.params}>
                {t('settings.classValuesLink')}
              </Link>
            )}
          </span>
        ) : entry.placeOverrides ? (
          <span className="console-muted">{t('settings.placeOverrides', { count: entry.placeOverrides })}</span>
        ) : null}
        {entry.venueValues !== undefined ? <VenueValues values={entry.venueValues} format={format} settingLabel={entry.label} /> : null}
      </div>
    </div>
  );
}

/** The organisation's screen: "3 venues use their own value", the list one press away. */
function VenueValues({
  values,
  format,
  settingLabel,
}: {
  readonly values: readonly { readonly name: string; readonly value: unknown }[];
  readonly format: (value: unknown) => string;
  readonly settingLabel: string;
}) {
  const { t } = useTranslation('console');
  const [open, setOpen] = useState(false);
  const id = `console-venue-values-${copyId(settingLabel).replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <div className="console-setting__venues">
      <Button variant="ghost" size="sm" aria-expanded={open} aria-controls={id} onClick={() => setOpen((current) => !current)}>
        {t('settings.venueValues', { count: values.length })}
      </Button>
      <ul id={id} hidden={!open} className="console-setting__venue-list">
        {values.map((place, index) => (
          <li key={`${place.name}:${index}`}>{t('settings.venueValue', { venue: place.name === '' ? t('settings.unknownVenue') : place.name, value: format(place.value) })}</li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// The 412 panel
// ---------------------------------------------------------------------------------------------

function ConflictPanel({
  rows,
  onReapply,
  onKeepTheirs,
}: {
  readonly rows: readonly ConflictRow[];
  readonly onReapply: () => void;
  readonly onKeepTheirs: () => void;
}) {
  const { t } = useTranslation('console');
  return (
    <section className="console-conflict" role="alert" aria-labelledby="console-conflict-title">
      <h3 id="console-conflict-title">{t('settings.conflict.title')}</h3>
      <p>{t('settings.conflict.body')}</p>
      <table className="console-conflict__table">
        <caption className="clav-visually-hidden">{t('settings.conflict.caption')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('settings.conflict.setting')}</th>
            <th scope="col">{t('settings.conflict.theirs')}</th>
            <th scope="col">{t('settings.conflict.yours')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.id}>
              <th scope="row">{row.label}</th>
              <td>{row.theirs}</td>
              <td>
                {row.yours}
                {row.nowReadOnly ? <span className="console-muted"> · {t('settings.conflict.nowReadOnly')}</span> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="console-conflict__actions">
        <Button variant="secondary" onClick={onKeepTheirs}>
          {t('settings.conflict.keepTheirs')}
        </Button>
        <Button variant="secondary" onClick={onReapply}>
          {t('settings.conflict.reapply')}
        </Button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------------------------

interface Section {
  readonly key: string;
  readonly title: string | undefined;
  readonly rows: readonly Row[];
}

/** Consecutive entries with the same `section` share a panel. */
function sectionsOf(rows: readonly Row[]): Section[] {
  const sections: { key: string; title: string | undefined; rows: Row[] }[] = [];
  for (const row of rows) {
    const last = sections.at(-1);
    if (last !== undefined && last.title === row.entry.section) last.rows.push(row);
    else sections.push({ key: `${sections.length}:${row.entry.section ?? ''}`, title: row.entry.section, rows: [row] });
  }
  return sections;
}

function labelForErrorKey(rows: readonly Row[], key: string): string {
  const row = rows.find((candidate) => key === candidate.entry.key || key.startsWith(`${candidate.entry.key}.`));
  return row?.entry.label ?? key;
}

type Translate = ReturnType<typeof useTranslation>['t'];

export function formatWords(t: Translate) {
  return {
    on: t('settings.value.on'),
    off: t('settings.value.off'),
    notSet: t('settings.value.notSet'),
    none: t('settings.value.none'),
    items: (count: number) => t('settings.value.items', { count }),
    unit: (key: string, options: Record<string, unknown>): string => t(key, options),
  };
}

/** How one item of a purpose-made list reads: 10 → "10 min", `mousetouchpad` → "Mouse and touchpad". */
function itemWords(entry: RenderableSetting, words: ReturnType<typeof formatWords>): ((item: string | number) => string) | undefined {
  switch (entry.control) {
    case 'minutesList':
    case 'spokenWarnings':
      return (item) => words.unit('settings.unit.shortMinutes', { count: item });
    case 'settingsPages':
      return (item) => words.unit(`settings.page.${String(item)}`, { defaultValue: String(item) });
    case 'rolePicker':
      return (item) => words.unit(`settings.role.${String(item)}`, { defaultValue: String(item) });
    default:
      return undefined;
  }
}

/** A value of an entry in words, in its display unit: grace 300 reads "5 minutes". */
export function formatEntry(value: unknown, entry: RenderableSetting, labels: SettingLabels, words: ReturnType<typeof formatWords>): string {
  const item = itemWords(entry, words);
  if (entry.control === 'updateWindow') return typeof value === 'string' ? value.replace('-', ' – ') : words.unit('settings.value.anyTime', {});
  if (Array.isArray(value) && value.length === 0) return words.none;
  if (Array.isArray(value) && item !== undefined) return value.map((one) => item(one as string | number)).join(', ');
  return formatValue(value, {
    entryKey: entry.key,
    schema: entry.schema,
    context: entry.context,
    labels,
    words,
    display: entry.display,
    withUnit: (number, display) => formatWithUnit(number, display, words.unit),
  });
}

export type { ValueSource };
